/**
 * 图片元素组件（与 Vue ImageElement.vue 对应）
 * - 双击进入「移动图片在容器内位置」模式，拖拽可调整 object-position。
 */
import type { ImageElement as ImageElementType } from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps } from '@/components/canvas/elements/types';
import { appendBlurFilter, composeContainerTransform } from '../group/containerTransform';
import { cornerRadiiToCssBorderRadius, resolveCornerRadiiPx } from '@/components/canvas/shared/utils/geometry/cornerRadius';
import { blurRadius, firstStroke } from '@/components/canvas/elements/model';

export interface ImageElementComponentProps extends ElementComponentProps<ImageElementType> {
  /** 双击图片时调用，进入「移动图片在容器内位置」拖拽模式 */
  onStartImagePositionDrag?: (element: ImageElementType, e: React.MouseEvent) => void;
}

function getObjectPositionValue(element: ImageElementType): string {
  const position = element.data.objectPosition;
  if (!position) return 'center';
  return `${position.x}% ${position.y}%`;
}

export function ImageElement({
  element,
  selected,
  dragOffset = { dx: 0, dy: 0 },
  onSelect,
  onMouseDown,
  onStartImagePositionDrag,
}: ImageElementComponentProps) {
  const { dx, dy } = dragOffset;
  const style: React.CSSProperties = {
    position: 'absolute',
    left: element.layout.x + dx,
    top: element.layout.y + dy,
    width: element.size.width,
    height: element.size.height,
    opacity: element.appearance.opacity,
    transform: composeContainerTransform(element),
    filter: appendBlurFilter(undefined, blurRadius(element)),
  };

  const objectPosition = getObjectPositionValue(element);
  const scale = element.data.imageScale ?? 1;
  const borderRadiusCss = cornerRadiiToCssBorderRadius(resolveCornerRadiiPx(element));
  const stroke = firstStroke(element.appearance);

  return (
    <div
      style={style}
      className="pointer-events-auto cursor-inherit"
      data-element-hit
      data-element-id={element.id}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(e);
      }}
      onMouseDown={(e) => onMouseDown?.(e, element)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!element.interaction.locked) onStartImagePositionDrag?.(element, e);
      }}
    >
      <div
        className="w-full h-full overflow-hidden"
        style={{ borderRadius: borderRadiusCss }}
      >
        <img
          src={element.data.src}
          alt=""
          className="h-full w-full object-cover"
          style={{
            objectFit: element.data.objectFit ?? 'cover',
            objectPosition,
            transform: scale !== 1 ? `scale(${scale})` : undefined,
            transformOrigin: objectPosition,
            width: '100%',
            height: '100%',
            border: stroke?.width
              ? `${stroke.width}px ${stroke.style ?? 'solid'} ${stroke.color ?? 'transparent'}`
              : undefined,
            borderRadius: borderRadiusCss,
          }}
          draggable={false}
        />
      </div>
    </div>
  );
}
